import {
  Badge,
  Button,
  buttonVariants,
  Card,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Store, UserRoundCog, Users } from "lucide-react";
import { AddUserDialog } from "../../components/add-user-dialog";
import { TenantBillingPanel } from "../../components/tenant-billing";
import { TenantFlagsCard } from "../../components/tenant-flags";
import { TenantNotesCard } from "../../components/tenant-notes";
import { TenantTimelineCard } from "../../components/tenant-timeline";
import { api, unwrap } from "../../lib/api";
import {
  ACCOUNT_STATUS_LABELS,
  CHANNEL_LABELS,
  formatDate,
  formatDateTime,
  MEMBER_ROLE_LABELS,
  pressLinkClassName,
  SUBSCRIPTION_LABELS,
  TENANT_STATUS,
  TOKEN_HEALTH_LABELS,
  usageLabel,
} from "../../lib/tenants";

export const Route = createFileRoute("/_admin/tenants_/$id")({
  component: TenantDetailPage,
});

const ROLES = ["owner", "admin", "member"] as const;

function TenantDetailPage() {
  const { id } = Route.useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const tenantQuery = useQuery({
    queryKey: ["admin", "tenants", id],
    queryFn: async () => unwrap(await api.admin.tenants({ id }).get()),
  });

  const impersonate = useMutation({
    mutationFn: async () =>
      unwrap(await api.admin.tenants({ id }).impersonate.post()),
    onSuccess: ({ webUrl }) => {
      window.open(webUrl, "_blank");
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      toast.add({
        type: "info",
        title: "Sessão de suporte iniciada",
        description: "A ação fica registrada na trilha de auditoria.",
      });
    },
  });

  const changeRole = useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: (typeof ROLES)[number];
    }) =>
      unwrap(
        await api.admin
          .tenants({ id })
          .members({ userId })
          .patch({ role }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      toast.add({ type: "success", title: "Papel atualizado" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível alterar o papel",
        description: error.message,
      });
    },
  });

  if (tenantQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5 text-ink-muted" />
      </div>
    );
  }

  const tenant = tenantQuery.data?.tenant;
  if (!tenant) {
    return (
      <Card>
        <EmptyState
          title="Organização não encontrada"
          description="Ela pode ter sido removida ou o endereço está errado."
          action={
            <Link to="/" className={buttonVariants({ variant: "secondary" })}>
              Voltar às organizações
            </Link>
          }
        />
      </Card>
    );
  }

  const status = TENANT_STATUS[tenant.status] ?? TENANT_STATUS.active!;

  return (
    <>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <Link to="/" className={`${pressLinkClassName} mb-1 text-2xs text-ink-muted`}>
            <span className="inline-flex items-center gap-1">
              <ArrowLeft className="size-3" />
              Organizações
            </span>
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="text-base font-semibold tracking-tight text-ink">
              {tenant.name}
            </h1>
            <Badge variant={status.variant} dot>
              {status.label}
            </Badge>
          </div>
          <p className="mt-0.5 font-mono text-2xs text-ink-muted">
            {tenant.slug}
            {tenant.plan ? ` · ${tenant.plan}` : ""}
            {tenant.subscriptionStatus
              ? ` · ${SUBSCRIPTION_LABELS[tenant.subscriptionStatus] ?? tenant.subscriptionStatus}`
              : ""}
            {tenant.currentPeriodEnd
              ? ` · até ${formatDate(tenant.currentPeriodEnd)}`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AddUserDialog tenantId={tenant.id} tenantName={tenant.name} />
          <Tooltip content="Entrar como suporte">
            <Button
              variant="secondary"
              loading={impersonate.isPending}
              onClick={() => impersonate.mutate()}
            >
              <UserRoundCog />
              Entrar como suporte
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Card className="animate-rise">
          <div className="border-b border-line px-4 py-3">
            <p className="text-[0.8125rem] font-semibold text-ink">Membros</p>
            <p className="mt-0.5 text-2xs text-ink-muted">
              {tenant.members.length} na organização. O último dono não pode
              perder o papel.
            </p>
          </div>
          {tenant.members.length === 0 ? (
            <EmptyState
              icon={<Users />}
              title="Nenhum membro"
              description="Adicione o primeiro usuário desta organização."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Pessoa</TableHead>
                  <TableHead>Desde</TableHead>
                  <TableHead className="w-40">Papel</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant.members.map((member) => (
                  <TableRow key={member.userId}>
                    <TableCell>
                      <p className="text-ink">{member.name}</p>
                      <p className="font-mono text-2xs text-ink-muted">
                        {member.email}
                      </p>
                    </TableCell>
                    <TableCell className="text-2xs text-ink-muted">
                      {formatDate(member.joinedAt)}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={member.role}
                        itemToStringLabel={(value) =>
                          MEMBER_ROLE_LABELS[value ?? ""] ?? String(value)
                        }
                        onValueChange={(value) => {
                          if (!value || value === member.role) return;
                          changeRole.mutate({
                            userId: member.userId,
                            role: value as (typeof ROLES)[number],
                          });
                        }}
                      >
                        <SelectTrigger className="min-w-28" />
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                              {MEMBER_ROLE_LABELS[role]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card className="animate-rise [animation-delay:40ms]">
          <div className="border-b border-line px-4 py-3">
            <p className="text-[0.8125rem] font-semibold text-ink">Contas</p>
            <p className="mt-0.5 text-2xs text-ink-muted">
              {usageLabel(tenant.usage.accounts, tenant.limits.accounts)} lojas
              · token e último sync
            </p>
          </div>
          {tenant.accounts.length === 0 ? (
            <EmptyState
              icon={<Store />}
              title="Nenhuma loja conectada"
              description="O vendedor ainda não autorizou uma conta de marketplace."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Loja</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>Último sync</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant.accounts.map((account) => {
                  const token =
                    TOKEN_HEALTH_LABELS[account.token.state] ??
                    TOKEN_HEALTH_LABELS.unknown!;
                  return (
                    <TableRow key={account.id}>
                      <TableCell>
                        <p className="text-ink">{account.displayName}</p>
                        <p className="font-mono text-2xs text-ink-muted">
                          {CHANNEL_LABELS[account.provider] ?? account.provider}
                          {account.externalAccountId
                            ? ` · ${account.externalAccountId}`
                            : ""}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            account.status === "connected" ? "success" : "warning"
                          }
                          dot
                        >
                          {ACCOUNT_STATUS_LABELS[account.status] ?? account.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={token.variant}>{token.label}</Badge>
                        {account.token.expiresAt && (
                          <p className="mt-0.5 text-2xs text-ink-muted">
                            {formatDateTime(account.token.expiresAt)}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-2xs text-ink-muted">
                        {account.lastSyncAt
                          ? formatDateTime(account.lastSyncAt)
                          : "Nunca"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card className="animate-rise [animation-delay:80ms]">
          <div className="border-b border-line px-4 py-3">
            <p className="text-[0.8125rem] font-semibold text-ink">Cobrança</p>
            <p className="mt-0.5 text-2xs text-ink-muted">
              Indicadores operacionais. A fatura em si fica na Asaas.
            </p>
          </div>
          <div className="p-4">
            <TenantBillingPanel tenant={tenant} events={tenant.asaasEvents} />
          </div>
        </Card>

        <TenantTimelineCard tenantId={tenant.id} />
        <TenantNotesCard
          tenantId={tenant.id}
          notes={tenant.notes}
          milestones={tenant.milestones}
        />
        <TenantFlagsCard tenantId={tenant.id} flags={tenant.flags} />
      </div>
    </>
  );
}
